import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Link from '@mui/material/Link';
import { ThemeProvider } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/core/hooks/useAuth';
import { getProfileImageURL } from '@/core/services/storage';
import { getUserWalletData } from '@/core/services/wallet';
import { darkTheme } from '@/core/theme';
import { BOTTOM_NAV_HEIGHT, BottomNav } from './BottomNav';
import { Header } from './Header';
import { SIDEBAR_WIDTH, Sidebar } from './Sidebar';

const GRADIENT = 'linear-gradient(135deg, #8a2be2, #FFD700)';

export function Layout() {
  const { user } = useAuth();
  const [profileNickname, setProfileNickname] = useState('');
  const [profileImageURL, setProfileImageURL] = useState(null);

  const fetchProfileSummary = useCallback(async () => {
    if (!user) return;
    try {
      const walletData = await getUserWalletData(user.uid);
      if (walletData) {
        setProfileNickname(walletData.nickname || user.uid.substring(0, 5));
        if (walletData.profileImage) {
          const url = await getProfileImageURL(walletData.profileImage);
          setProfileImageURL(url);
        }
      }
    } catch (err) {
      console.error('Failed to fetch profile summary:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchProfileSummary();
  }, [user, fetchProfileSummary]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >
        {/* Header (PC/モバイル共通) */}
        <Header />

        {/* Middle: Sidebar + Content */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* Sidebar (PC only) */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Sidebar nickname={profileNickname} profileImageURL={profileImageURL} />
          </Box>

          {/* Content */}
          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              px: { xs: 2, md: 4 },
              py: 3,
              pb: { xs: `${BOTTOM_NAV_HEIGHT + 16}px`, md: 3 },
            }}
          >
            <Outlet context={{ refreshProfile: fetchProfileSummary }} />
          </Box>
        </Box>

        {/* Footer (PC only) */}
        <Box
          component="footer"
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            height: 52,
            flexShrink: 0,
            bgcolor: '#0A0A0F',
            borderTop: '1px solid',
            borderImage: `${GRADIENT} 1`,
            position: 'relative',
            px: 3,
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
            &copy;2026 Swandive, Inc.
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              right: 24,
              display: 'flex',
              gap: 3,
            }}
          >
            <Link
              href="https://swandive.co.jp"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', fontSize: '0.7rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}
            >
              運営会社
            </Link>
            <Link
              href="https://2dot99.com/tos.html"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', fontSize: '0.7rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}
            >
              利用規約
            </Link>
            <Link
              href="https://2dot99.com/privacypolicy.html"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', fontSize: '0.7rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}
            >
              プライバシーポリシー
            </Link>
          </Box>
        </Box>

        {/* BottomNav (モバイル only) */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <BottomNav profileImageURL={profileImageURL} />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
